import React, { useEffect, useState } from 'react';
import Textarea from 'react-textarea-autosize';

import { useStore } from '../../store';

import UserAvatar from '../UserAvatar';

import { formatDateTz } from '../../helpers';
import { isUserLoggedIn } from '../../helpers/auth';

import type { Comment } from '../../types/comment';

import '@awesome.me/webawesome/dist/components/dialog/dialog.js';

interface MemoryCommentsItemProps {
  comment: Comment;
  isPending: boolean;
  handlePostComment: (params: { content: string; parent_comment_id?: number }) => void;
  handleUpdateComment: (params: { commentId: number; content: string }) => void;
  handleDeleteComment: (commentId: number) => void;
  readOnly?: boolean;
}

const MemoryCommentsItem: React.FC<MemoryCommentsItemProps> = ({
  comment,
  isPending,
  handlePostComment,
  handleUpdateComment,
  handleDeleteComment,
  readOnly = false,
}) => {
  const [content, setContent] = useState('');
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const { profile } = useStore((state) => state);

  useEffect(() => {
    if (!isPending && waiting) {
      setReplying(false);
      setEditing(false);
    } else {
      setWaiting(true);
    }
  }, [isPending]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  const handleSave = () => {
    if (editing) {
      handleUpdateComment({ commentId: comment.id, content });
    } else {
      handlePostComment({ content, parent_comment_id: comment.id });
    }
  };

  const switchReplying = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setContent('');
    setReplying((replying) => !replying);
  };

  const switchEditing = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (editing) {
    } else {
      setContent(comment.content);
    }
    setEditing((editing) => !editing);
  };

  return (
    <div id={`comment-${comment.id}`} className={`memory-comment wa-flank wa-flank:start wa-gap-0 wa-align-items-start ${comment.updated ? 'memory-comment-blink' : ''}`} style={{ '--flank-size': '45px' } as React.CSSProperties}>
      <div className="memory-comment-sidebar">
          <UserAvatar profile={comment.user} />
      </div>
      <div className="memory-comment-container wa-stack wa-gap-2xs">
        <div className="memory-comment-header wa-cluster wa-gap-2xs">
          <strong>{comment.user.name}</strong>
          {comment.user.id === profile?.id && <span>(you)</span>}
          <span title={formatDateTz(comment.created_at, 'YYYY-MM-DD HH:mm:ss')}>
            {formatDateTz(comment.created_at, 'relative')}
          </span>
          {(comment.updated_at && comment.updated_at !== comment.created_at) && (
            <span title={formatDateTz(comment.updated_at, 'YYYY-MM-DD HH:mm:ss')}>
              (edited {formatDateTz(comment.updated_at, 'relative')})
            </span>
          )}
        </div>
        <div className="memory-comment-content">
          {comment.content}
        </div>
        {(!readOnly && isUserLoggedIn()) && (
          <>
            <div className="memory-comment-actions wa-cluster wa-gap-xs">
              {!editing && (
                <div className="memory-comment-action memory-comment-reply">
                  {replying ? (
                    <a href="#reply-comment" onClick={switchReplying}>
                      Cancel
                    </a>
                  ) : (
                    <a href="#reply-comment" onClick={switchReplying}>
                      Reply
                    </a>
                  )}
                </div>
              )}
              {(!replying && profile?.id === comment.user.id) && (
                <div className="memory-comment-action memory-comment-edit">
                  {editing ? (
                    <a href="#edit-comment" onClick={switchEditing}>
                      Cancel
                    </a>
                  ) : (
                    <a href="#edit-comment" onClick={switchEditing}>
                      Edit
                    </a>
                  )}
                </div>
              )}
              {(!replying && !editing && profile?.id === comment.user.id) && (
                <div className="memory-comment-action memory-comment-delete">
                  <wa-dialog label="Are you sure?" id={`dialog-delete-${comment.id}`}>
                    Are you sure you want to delete this comment? It cannot be undone.
                    <wa-button
                      slot="footer"
                      variant="brand"
                      data-dialog="confirm"
                      onClick={() => {
                        handleDeleteComment(comment.id);
                        const dialog = document.getElementById(`dialog-delete-${comment.id}`) as HTMLDialogElement | null;
                        if (dialog) {
                          dialog.open = false;
                        }
                      }}
                    >
                      Delete
                    </wa-button>
                    <wa-button slot="footer" variant="brand" data-dialog="close">Cancel</wa-button>
                  </wa-dialog>
                  <a href="#delete-comment" data-dialog={`open dialog-delete-${comment.id}`} onClick={(e) => { e.preventDefault(); }}>
                    Delete
                  </a>
                </div>
              )}
            </div>
            {(replying || editing) && (
              <div className="memory-comment-form">
                <Textarea
                  id={`reply-to-${comment.id}`}
                  name={`reply-to-${comment.id}`}
                  onChange={handleChange}
                  minRows={2}
                  disabled={isPending}
                  value={content}
                />
                <wa-button onClick={handleSave} size="s" disabled={isPending || content.trim() === ''}>
                  {editing ? isPending ? 'Saving...' : 'Save' : isPending ? 'Posting...' : 'Reply'}
                </wa-button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MemoryCommentsItem;
